import React from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Moon, Sun, Menu, X, Globe, User, LogOut, Settings, Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase/config';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useLocalStorage } from '../hooks/useLocalStorage';

export function Header() {
  const [isDark, setIsDark] = React.useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
      return true;
    }
    return false;
  });
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = React.useState(false);
  const [user] = useAuthState(auth);
  const [showUserMenu, setShowUserMenu] = React.useState(false);
  const { t, i18n } = useTranslation();
  const [soundEnabled, setSoundEnabled] = useLocalStorage<boolean>('sound-enabled', true);
  const [showSettingsMenu, setShowSettingsMenu] = React.useState(false);
  const navigate = useNavigate();

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);
    document.documentElement.classList.toggle('dark');
    const newTheme = newIsDark ? 'dark' : 'light';
    localStorage.setItem('theme', newTheme);

    // Dispatch a custom event to notify other components
    window.dispatchEvent(
      new CustomEvent('settings-changed', {
        detail: { key: 'theme', value: newTheme }
      })
    );

    setShowSettingsMenu(false);
  };

  const toggleSound = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);

    // Dispatch a custom event
    window.dispatchEvent(
      new CustomEvent('settings-changed', {
        detail: { key: 'sound-enabled', value: String(newSoundEnabled) }
      })
    );

    setShowSettingsMenu(false);
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    setShowLanguageSelector(false);
  };

  // Initialize language from localStorage
  React.useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // Add this useEffect to ensure the UI updates when the theme changes
  React.useEffect(() => {
    // This will run every time isDark changes
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // Add a custom event listener for settings changes
  React.useEffect(() => {
    // Function to handle settings changes
    const handleSettingsChange = (event: CustomEvent) => {
      const { key, value } = event.detail;

      if (key === 'theme') {
        setIsDark(value === 'dark');
      }
      if (key === 'sound-enabled') {
        setSoundEnabled(value === 'true');
      }
    };

    // Add event listener for our custom event
    window.addEventListener('settings-changed', handleSettingsChange as EventListener);

    // Clean up
    return () => {
      window.removeEventListener('settings-changed', handleSettingsChange as EventListener);
    };
  }, [setSoundEnabled]);

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/sudoku');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header onKeyDown={(event) => { if (event.key === 'Escape') { event.currentTarget.querySelector<HTMLButtonElement>('button[aria-expanded="true"]')?.focus(); setIsMenuOpen(false); setShowSettingsMenu(false); setShowUserMenu(false); setShowLanguageSelector(false); } }} className="border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-midnight">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 min-h-20 flex items-center justify-between">
        <div className="flex items-center space-x-1 md:space-x-2">
          <Link to="/" aria-label="Doganju" className="flex min-h-11 shrink-0 items-center gap-1 md:gap-2">
            <img src="/assets/images/doganju-small.png" alt="" width={32} height={32} className="h-6 w-6 md:h-8 md:w-8" />
            <img src="/assets/brand/wordmark-light.svg" alt="" width={152} height={40} className="h-auto w-[100px] dark:hidden sm:h-10 sm:w-[152px]" />
            <img src="/assets/brand/wordmark-dark.svg" alt="" width={152} height={40} className="hidden h-auto w-[100px] dark:block sm:h-10 sm:w-[152px]" />
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-2">
          <NavLink to="/sudoku" className="min-h-11 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 aria-[current=page]:bg-indigo-50 aria-[current=page]:text-cobalt dark:aria-[current=page]:bg-slate-800 dark:aria-[current=page]:text-indigo-200" tabIndex={0}>
            {t('sudoku')}
          </NavLink>
          <NavLink to="/15puzzle" className="min-h-11 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 aria-[current=page]:bg-indigo-50 aria-[current=page]:text-cobalt dark:aria-[current=page]:bg-slate-800 dark:aria-[current=page]:text-indigo-200" tabIndex={0}>
            {t('15puzzle')}
          </NavLink>
          <NavLink to="/2048" className="min-h-11 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 aria-[current=page]:bg-indigo-50 aria-[current=page]:text-cobalt dark:aria-[current=page]:bg-slate-800 dark:aria-[current=page]:text-indigo-200" tabIndex={0}>
            {t('2048')}
          </NavLink>
          {/* <Link to="/hangman" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-lg">
            {t('hangman')}
          </Link> */}
          <NavLink to="/about" className="min-h-11 rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 aria-[current=page]:bg-indigo-50 aria-[current=page]:text-cobalt dark:aria-[current=page]:bg-slate-800 dark:aria-[current=page]:text-indigo-200" tabIndex={0}>
            {t('about')}
          </NavLink>
        </nav>

        <div className="flex lg:hidden items-center justify-end space-x-2 flex-1">
          <button
            className="min-h-11 min-w-11 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={t('brand.menu')}
          >
            {isMenuOpen ? (
              <X className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            ) : (
              <Menu className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            )}
          </button>
        </div>

        <div className="flex items-center">
          {/* Settings buttons for larger screens */}
          <div className="hidden lg:flex items-center space-x-2">
            <button
              onClick={toggleSound}
              className="min-h-11 min-w-11 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label={t('brand.sound')}
            >
              {soundEnabled ? (
                <Volume2 className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <VolumeX className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>

            <button
              onClick={toggleTheme}
              className="min-h-11 min-w-11 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label={t('brand.theme')}
            >
              {isDark ? (
                <Moon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <Sun className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>

            <div className="relative">
              <button
                aria-expanded={showLanguageSelector}
                onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                className="min-h-11 min-w-11 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label={t('brand.language')}
              >
                <Globe className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </button>

              {showLanguageSelector && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10">
                  <div className="py-1">
                    <button
                      onClick={() => changeLanguage('en')}
                      className="min-h-11 flex items-center justify-between w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <span>English</span>
                      {i18n.language === 'en' && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => changeLanguage('es')}
                      className="min-h-11 flex items-center justify-between w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <span>Español</span>
                      {i18n.language === 'es' && (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                    {/* Uncomment if you want to add these languages back
                    <button onClick={() => changeLanguage('fr')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                      Français
                    </button>
                    <button onClick={() => changeLanguage('de')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                      Deutsch
                    </button>
                    <button onClick={() => changeLanguage('zh')} className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                      中文
                    </button> */}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Settings menu button for mobile */}
          <div className="lg:hidden flex items-center mr-2">
            <button
              className="min-h-11 min-w-11 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-expanded={showSettingsMenu}
              onClick={() => setShowSettingsMenu(!showSettingsMenu)}
              aria-label={t('brand.settings')}
            >
              <Settings className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          <div className="ml-1 md:ml-2">
            {user ? (
              <div className="relative">
                <button
                  aria-expanded={showUserMenu}
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-1 min-h-11 min-w-11 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label={t('brand.account')}
                >
                  <div className="h-5 w-5 md:h-7 md:w-7 rounded-full bg-cobalt flex items-center justify-center text-white">
                    {user.photoURL ? (
                      <img src={user.photoURL} alt="" className="h-5 w-5 md:h-7 md:w-7 rounded-full" />
                    ) : (
                      <User className="h-3 w-3 md:h-4 md:w-4" />
                    )}
                  </div>
                  <span className="hidden lg:inline text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[40px] md:max-w-[80px]">
                    {user.displayName || user.email?.split('@')[0] || t('brand.account')}
                  </span>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10">
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <User className="h-4 w-4 mr-2" />
                        {t('profile', 'Profile')}
                      </Link>
                      {/* <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Settings className="h-4 w-4 mr-2" />
                        {t('settings', 'Settings')}
                      </Link> */}
                      <button
                        onClick={() => {
                          handleSignOut();
                          setShowUserMenu(false);
                        }}
                        className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        {t('signout', 'Sign out')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex space-x-0.5 md:space-x-2">
                <Link
                  to="/login"
                  className="inline-flex min-h-11 items-center rounded-lg bg-cobalt px-3 text-sm font-semibold text-white hover:bg-blue-800"
                >
                  {t('login', 'Login')}
                </Link>
                <Link
                  to="/signup"
                  className="hidden sm:inline-flex min-h-11 items-center rounded-lg border border-cobalt px-3 text-sm font-semibold text-cobalt hover:bg-indigo-50 dark:border-indigo-300 dark:text-indigo-200 dark:hover:bg-slate-800"
                >
                  {t('signup', 'Sign up')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile navigation menu */}
      {isMenuOpen && (
        <div className="lg:hidden">
          <nav className="px-4 pt-2 pb-4 space-y-2 bg-white dark:bg-gray-900 shadow-lg">
            <Link
              to="/sudoku"
              className="block text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-lg py-2"
              onClick={() => setIsMenuOpen(false)}
              tabIndex={0}
            >
              {t('sudoku')}
            </Link>
            <Link
              to="/15puzzle"
              className="block text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-lg py-2"
              onClick={() => setIsMenuOpen(false)}
              tabIndex={0}
            >
              {t('15puzzle')}
            </Link>
            <Link
              to="/2048"
              className="block text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-lg py-2"
              onClick={() => setIsMenuOpen(false)}
              tabIndex={0}
            >
              {t('2048')}
            </Link>
            {/* <Link
              to="/hangman"
              className="block text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-lg py-2"
              onClick={() => setIsMenuOpen(false)}
              tabIndex={0}
            >
              {t('hangman')}
            </Link> */}
            <Link
              to="/about"
              className="block text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-lg py-2"
              onClick={() => setIsMenuOpen(false)}
              tabIndex={0}
            >
              {t('about')}
            </Link>
          </nav>
        </div>
      )}

      {/* Mobile settings menu */}
      {showSettingsMenu && (
        <div className="lg:hidden">
          <div className="px-4 pt-2 pb-4 space-y-2 bg-white dark:bg-gray-900 shadow-lg">
            <button
              onClick={toggleSound}
              className="flex items-center w-full text-left text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-lg py-2"
            >
              {soundEnabled ? (
                <Volume2 className="h-5 w-5 mr-2" />
              ) : (
                <VolumeX className="h-5 w-5 mr-2" />
              )}
              {soundEnabled ? t('soundOn') : t('soundOff')}
            </button>

            <button
              onClick={toggleTheme}
              className="flex items-center w-full text-left text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 text-lg py-2"
            >
              {isDark ? (
                <Moon className="h-5 w-5 mr-2" />
              ) : (
                <Sun className="h-5 w-5 mr-2" />
              )}
              {isDark ? t('darkMode') : t('lightMode')}
            </button>

            <div className="py-2">
              <div className="flex items-center text-gray-600 dark:text-gray-300 text-lg mb-2">
                <Globe className="h-5 w-5 mr-2" />
                {t('language', 'Language')}
              </div>
              <div className="pl-7 space-y-2">
                <button
                  onClick={() => {
                    changeLanguage('en');
                    setShowSettingsMenu(false);
                  }}
                  className="min-h-11 flex items-center justify-between w-full text-left text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  <span>English</span>
                  {i18n.language === 'en' && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => {
                    changeLanguage('es');
                    setShowSettingsMenu(false);
                  }}
                  className="min-h-11 flex items-center justify-between w-full text-left text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400"
                >
                  <span>Español</span>
                  {i18n.language === 'es' && (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
